// frontend/src/features/inventory/pages/WarehouseListPage.tsx
// ADR-074: لیست انبارها + دکمه ایجاد انبار جدید

import { useState } from "react";
import { usePermission } from "@/auth/usePermission";
import { useNavigate } from "react-router-dom";
import {
  useV1InventoryWarehousesList,
  useV1InventoryWarehousesCreate,
  useV1InventoryWarehousesPartialUpdate,
  v1InventoryWarehousesList,
} from "@/api/generated/v1/v1";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { setCustomFieldValues } from "@/features/customFields/api";
import { PageBanner } from "@/shared/components/PageBanner";

export function WarehouseListPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();

  // state فرم ایجاد انبار
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});
  const [customFieldsValid, setCustomFieldsValid] = useState(true);

  // state فرم فیلدهای سفارشی موجود
  const [customFieldsModalId, setCustomFieldsModalId] = useState<string | null>(null);

  // state ویرایش انبار
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useV1InventoryWarehousesList(
    { search: searchTerm || undefined, ...activeFilters } as any,
    { query: {} }
  );

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1InventoryWarehousesList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters
      } as any);
      if ((res as any).truncated) {
        alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
      }
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { header: "کد", accessor: (row: any) => row.code },
    { header: "نام انبار", accessor: (row: any) => row.name },
    { header: "وضعیت", accessor: (row: any) => row.is_active ? "فعال" : "غیرفعال" },
  ];

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "warehouses_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "warehouses_export");
  };

  const { mutate, isPending } = useV1InventoryWarehousesCreate({
    mutation: {
      onSuccess: async (data: any) => {
        if (Object.keys(customFieldValues).length > 0) {
          await setCustomFieldValues("inventory.warehouse", data.id, customFieldValues);
        }
        setShowForm(false);
        setCode("");
        setName("");
        setError(null);
        refetch();
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.code?.[0] ||
          "خطا در ایجاد انبار"
        );
      },
    },
  });

  const { mutate: updateWarehouse, isPending: isUpdating } = useV1InventoryWarehousesPartialUpdate({
    mutation: {
      onSuccess: () => {
        setEditingId(null);
        setEditName("");
        setEditError(null);
        refetch();
      },
      onError: (err: any) => {
        setEditError(
          err?.response?.data?.detail ||
          "خطا در ویرایش انبار"
        );
      },
    },
  });

  const handleSubmit = () => {
    setError(null);
    if (!code.trim()) { setError("کد انبار الزامی است"); return; }
    if (!name.trim()) { setError("نام انبار الزامی است"); return; }
    if (!customFieldsValid) { setError("لطفاً فیلدهای سفارشی الزامی را پر کنید."); return; }
    mutate({ data: { code: code.trim().toUpperCase(), name: name.trim(), is_active: true } });
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="انبارها (Warehouses)"
        subtitle="مدیریت انبارها و ساختار مکان‌های ذخیره‌سازی"
        icon="🏭"
        helpLink="/help/inventory/warehouse-list"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
          {canManage && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ایجاد انبار جدید</span>
            </button>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        <DataTableToolbar
          onSearch={setSearchTerm}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          filters={[
            {
              name: "is_active",
              label: "وضعیت",
              options: [
                { label: "فعال", value: "True" },
                { label: "غیرفعال", value: "False" },
              ],
            },
          ]}
        />

        {/* فرم ایجاد انبار — inline */}
        {showForm && (
          <div
            className="mb-6 p-4"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--glass-border)",
              borderRadius: "10px",
            }}
          >
            <h2 className="font-bold mb-4">انبار جدید</h2>

            {error && (
              <div
                className="mb-3 p-3"
                style={{
                  background: "rgba(255,0,0,0.1)",
                  borderLeft: "4px solid red",
                  borderRadius: "8px",
                  color: "red",
                  fontSize: "0.9rem",
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  کد انبار *
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="مثال: WH-01"
                  dir="ltr"
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 2, minWidth: "200px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  نام انبار *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="مثال: انبار مرکزی"
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div className="mt-4 border-t border-gray-100/10 pt-4">
              <CustomFieldsPanel
                targetContentType="inventory.warehouse"
                hideSaveButton={true}
                onChange={(values, isValid) => {
                  setCustomFieldValuesState(values);
                  setCustomFieldsValid(isValid);
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending ? "در حال ذخیره..." : "ذخیره"}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowForm(false); setError(null); }}
              >
                انصراف
              </button>
            </div>
          </div>
        )}

        {/* لیست */}
        {isLoading && (
          <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
        )}

        {isError && (
          <p style={{ color: "red" }}>خطا در دریافت لیست انبارها.</p>
        )}

        {!isLoading && !isError && (
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "right" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--glass-border)", background: "rgba(255,255,255,0.04)" }}>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>کد</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>نام</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600" }}>وضعیت</th>
                <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "600", textAlign: "left" }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {(data?.results ?? []).length === 0 ? (
                <tr><td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>هیچ انباری ثبت نشده.</td></tr>
              ) : (
                (data?.results ?? []).map((wh) => (
                  <tr key={wh.id} style={{
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}>
                    <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace" }} dir="ltr">{wh.code}</td>
                    <td style={{ padding: "0.85rem 1rem" }}>
                      {editingId === wh.id ? (
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            style={{
                              padding: "0.4rem 0.6rem",
                              background: "rgba(255,255,255,0.08)",
                              border: "1px solid var(--glass-border)",
                              borderRadius: "6px",
                              color: "var(--color-text)",
                            }}
                          />
                          <button
                            className="btn btn-primary"
                            style={{ padding: "0.3rem 0.6rem", fontSize: "0.85rem" }}
                            onClick={() => {
                              if (!editName.trim()) return;
                              updateWarehouse({ id: wh.id, data: { name: editName.trim() } });
                            }}
                            disabled={isUpdating}
                          >
                            ذخیره
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.3rem 0.6rem", fontSize: "0.85rem" }}
                            onClick={() => setEditingId(null)}
                          >
                            انصراف
                          </button>
                        </div>
                      ) : (
                        wh.name
                      )}
                      {editingId === wh.id && editError && (
                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: "0.25rem" }}>{editError}</div>
                      )}
                    </td>
                    <td style={{ padding: "0.85rem 1rem" }}>
                      <span style={{
                        padding: "0.2rem 0.6rem", borderRadius: "100px",
                        background: wh.is_active ? "rgba(0,255,0,0.1)" : "rgba(255,0,0,0.1)",
                        color: wh.is_active ? "var(--color-success)" : "red", fontSize: "0.85rem",
                      }}>
                        {wh.is_active ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                      {canManage && editingId !== wh.id && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "0.2rem 0.6rem", fontSize: "0.85rem" }}
                          onClick={() => {
                            setEditingId(wh.id);
                            setEditName(wh.name);
                            setEditError(null);
                          }}
                        >
                          ویرایش نام
                        </button>
                      )}
                      {canManage && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "0.2rem 0.6rem", fontSize: "0.85rem", marginRight: "0.5rem" }}
                          onClick={() => setCustomFieldsModalId(wh.id)}
                        >
                          فیلدهای سفارشی
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal for viewing/editing custom fields of an existing warehouse */}
      {customFieldsModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="glass-panel w-full max-w-2xl p-6 relative">
            <button 
              className="absolute top-4 left-4 text-gray-400 hover:text-white"
              onClick={() => setCustomFieldsModalId(null)}
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-6">فیلدهای سفارشی انبار</h2>
            <CustomFieldsPanel
              targetContentType="inventory.warehouse"
              targetObjectId={customFieldsModalId}
              editable={canManage}
            />
          </div>
        </div>
      )}
    </div>
  );
}